import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import { selectWorkspaceConfirmationModalContext } from "features/content-overlay/selectors"
import {
    deleteTag,
    deleteWorkspace,
    resetWorkspaceTree,
    selectActiveWorkspaceId,
    selectFilteredTagIds,
    setActiveTagAction,
    setFilteredTagIds,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { deleteFire } from "features/entity-caching/fires/thunks"
import { removeMemberFromWorkspace } from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { capitalize, noop } from "nodash"
import { selectUserID } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    WorkspaceConfirmationModal,
    WorkspaceConfirmationModalProps,
} from "./WorkspaceConfirmationModal"
import { ScrollableArea } from "@nova/component-scrollable-area"
import * as Icons from "@nova/component-icons"
import { WorkspaceCollaboratorProfile } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { updateLicense } from "features/entity-caching/licenses/thunks"
import { formatSubscriptionType } from "features/data-management/workspace/subscriptions/module"
import {
    invalidateIntegrationAuthorizations,
    useDeleteIntegrationAuthorization,
} from "features/data-management/integrations/useIntegrations"
import { HotspotStatus, WorkspaceConfirmationModalName } from "@nova/constants"
import { useCallback } from "react"
import { selectAllFoldersById } from "features/data-management/workspace/context/selectors"
import {
    batchUpdateHotspotsAction,
    updateHotspot,
} from "features/pubsub/entityActions/cubes/hotspot"
import { closeHotspotMultiRemoval } from "features/data-management/hotspots/slice"
import {
    selectHotspotMultiSelectFlight,
    selectMultiSelectHotspots,
} from "features/data-management/hotspots/selectors"
import { selectMergedSiblingFlightIds } from "panels/hotspot/selectors"
import { useDeleteComment } from "features/comments/Comments"
import { useRouter } from "pages"
import { listAllSubChains } from "features/data-management/workspace/module"

//I don't particularly want to squeeze the useTranslation hook into this
const workspaceConfirmationModalMap: Record<
    WorkspaceConfirmationModalName,
    WorkspaceConfirmationModalProps
> = {
    [WorkspaceConfirmationModalName.LeaveWorkspace]: {
        getTitle: (workspaceName) => `Leave "${workspaceName}"?`,
        icon: { side: "right", icon: Icons.Exit },
        getConfirmText: () => "Leave Workspace",
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: () => (
            <>
                <h4 className="text-center">
                    You&apos;ll no longer have access to anything in this workspace.
                </h4>
                <h4 className="mt-4 text-center text-red-500">This cannot be undone.</h4>
            </>
        ),
    },
    [WorkspaceConfirmationModalName.RemoveCollaborators]: {
        title: "Remove Collaborators?",
        getConfirmText: () => "Remove Collaborators",
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: ({ collaborators }) => (
            <div>
                <h5>Remove these collaborators?</h5>
                <ScrollableArea className="flex flex-col gap-4 max-h-[21.25rem] mt-6 rounded-lg p-4 border border-gray-700">
                    {collaborators?.map((collaborator) => (
                        <WorkspaceCollaboratorProfile
                            key={collaborator.userId}
                            userId={collaborator.userId}
                            email={collaborator.email}
                            fullName={collaborator.fullName}
                        />
                    ))}
                </ScrollableArea>
            </div>
        ),
    },
    [WorkspaceConfirmationModalName.DeleteProject]: {
        getTitle: (projectName) => `Delete "${projectName}"?`,
        getConfirmText: () => "Delete",
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: ({ entityName }) => (
            <>
                <h4 className="text-center px-6">
                    Are you sure you want to permanently delete {entityName}?
                </h4>
                <h4 className="mt-4 text-center text-red-500">This cannot be undone.</h4>
            </>
        ),
    },
    [WorkspaceConfirmationModalName.DeleteFolder]: {
        getTitle: (folderName) => `Delete "${folderName}"?`,
        getConfirmText: () => "Delete",
        requiresInput: true,
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: ({ entityName, inputText, setInputText }) => (
            <>
                <h4 className="text-center">
                    This will permanently delete all subfolders and projects.
                </h4>
                <h4 className="my-4 text-center text-red-500">This cannot be undone.</h4>
                <WorkspaceTextInput
                    value={inputText}
                    handleChange={(value) => setInputText?.(value)}
                    className="!h-[2.5rem]"
                    inputClassName="!w-full !h-[2.5rem] !bg-gray-700 !border-none !p-2 !pl-4 !rounded-md"
                    name="folder-name"
                    autoFocus
                />
                <h5 className="mt-2 text-center text-gray-100">
                    Type the name &quot;{entityName}&quot; to delete this folder.
                </h5>
            </>
        ),
    },
    [WorkspaceConfirmationModalName.DeleteWorkspace]: {
        getTitle: (workspaceName) => `Delete "${workspaceName}"?`,
        getConfirmText: () => "Delete Workspace",
        requiresInput: true,
        isDestructive: true,
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: ({ entityName, inputText, setInputText }) => (
            <>
                <h4 className="text-center">
                    This will permanently delete everything in this workspace.
                </h4>
                <h4 className="my-4 text-center text-red-500">This cannot be undone.</h4>
                <WorkspaceTextInput
                    value={inputText}
                    handleChange={(value) => setInputText?.(value)}
                    name="workspace-name"
                    autoFocus
                />
                <h5 className="mt-2 text-center text-gray-100">
                    Type the name &quot;{entityName}&quot; to delete this workspace.
                </h5>
            </>
        ),
    },
    [WorkspaceConfirmationModalName.UnassignSubscription]: {
        getTitle: (userName, isAddOn) =>
            `Unassign ${isAddOn ? "Add-on" : "License"} from ${userName}?`,
        getConfirmText: () => "Unassign Subscription",
        onCancel: noop,
        onConfirm: noop,
        confirmationContentComponent: ({ license, entityName }) => (
            <h4>
                This will unassign a {formatSubscriptionType(license!.subscriptionType, true)}{" "}
                {capitalize(license?.isAddOn ? "add-on" : "license")} from {entityName}.
            </h4>
        ),
        icon: { side: "left", icon: Icons.PersonRemove },
    },
    [WorkspaceConfirmationModalName.DisconnectIntegration]: {
        getTitle: (title) => `Disconnect from ${title}`,
        confirmationContentComponent: ({ entityName }) => (
            <h4 className="text-center">
                Disconnecting will revoke your access to {entityName}. You can reconnect it at any
                time.
            </h4>
        ),
        icon: { side: "right", icon: Icons.Exit },
        getConfirmText: () => "Disconnect",
        onConfirm: noop,
        onCancel: noop,
    },
    [WorkspaceConfirmationModalName.DeleteHotspot]: {
        getTitle: (title) => `Remove Hotspot ${title}?`,
        confirmationContentComponent: ({}) => (
            <h4 className="text-center">
                Removing this hotspot means it will no longer be visible on your maps.
            </h4>
        ),
        icon: { side: "left", icon: Icons.Trash },
        getConfirmText: () => "Remove Hotspot",
        onCancel: noop,
        onConfirm: noop,
    },
    [WorkspaceConfirmationModalName.DeleteMultipleHotspots]: {
        getTitle: (title) => `Remove ${title} Hotspot${Number(title) > 1 ? "s" : ""}?`,
        confirmationContentComponent: ({}) => (
            <h4 className="text-center">
                Removing hotspots means they will no longer be visible on your maps.
            </h4>
        ),
        icon: { side: "left", icon: Icons.Trash },
        getConfirmText: ({ entityName }) =>
            `Remove ${entityName} Hotspot${Number(entityName) > 1 ? "s" : ""}`,
        onCancel: noop,
        onConfirm: noop,
    },
    [WorkspaceConfirmationModalName.DeleteColorFinderComment]: {
        title: "Delete Comment?",
        confirmationContentComponent: () => (
            <h4 className="text-center">
                Are you sure you want to delete this comment? This action can&apos;t be undone.
            </h4>
        ),
        icon: { side: "right", icon: Icons.Trash },
        getConfirmText: () => "Delete",
        onConfirm: noop,
        onCancel: noop,
    },
}

/**
 * A centralized place to hold all the different confirmation modals we show
 * Add more to the Map above if needed, need to write the handlers in the component so we can leverage Redux
 */
export const WorkspaceConfirmationModalRegistry = () => {
    const dispatch = useAppDispatch()
    const router = useRouter("/")
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const userId = useAppSelector(selectUserID)
    const workspaceFilteredTagIds = useAppSelector(selectFilteredTagIds())
    const allFoldersById = useAppSelector(selectAllFoldersById)
    const allSubEntitiesByFolderId = useCallback(
        (folderId: string) => {
            const folder = allFoldersById[folderId]
            if (!folder) return { subChains: [] as string[][], projectIds: [] as string[] }
            const projectIds = new Set<string>()
            const subChains = listAllSubChains(folder)
            subChains.forEach((chain) => {
                const leafId = chain.at(-1)
                if (leafId) allFoldersById[leafId]?.projects.forEach(({ id }) => projectIds.add(id))
            })
            return { subChains, projectIds: Array.from(projectIds) }
        },
        [allFoldersById]
    )
    const { trigger: deleteIntegrationAuthorization } = useDeleteIntegrationAuthorization()
    const { deleteComment } = useDeleteComment()
    const multiSelectedHotspots = useAppSelector(selectMultiSelectHotspots)
    const multiSelectFlightId = useAppSelector(selectHotspotMultiSelectFlight)
    const multiSelectFlightIds = useAppSelector(selectMergedSiblingFlightIds(multiSelectFlightId))

    const {
        type: workspaceConfirmationType,
        entityId,
        entityName,
        secondaryKey,
        collaborators,
        layers,
        groups,
        license,
        entityIds,
    } = useAppSelector(selectWorkspaceConfirmationModalContext)

    if (!entityId && !layers && !groups) {
        throw "Must provide a list of layers or an entity ID"
    }

    if (!workspaceConfirmationType) {
        throw "Tried to show Workspace Confirmation Modal without specifying a Type"
    }
    const props = workspaceConfirmationModalMap[workspaceConfirmationType]
    let onConfirm = props.onConfirm
    const onCancel = props.onCancel

    //Leave Workspace Handlers
    if (workspaceConfirmationType === WorkspaceConfirmationModalName.LeaveWorkspace) {
        onConfirm = async () => {
            if (!userId) return
            dispatch(resetWorkspaceTree())
            await dispatch(
                removeMemberFromWorkspace({
                    workspaceId: entityId!,
                    userId,
                    isSelf: true,
                })
            )
            dispatch(
                setGlobalNotificationContext({
                    message: "You have left the workspace",
                    status: GlobalNotificationStatus.default,
                    timeoutInMs: 6000,
                })
            )
            localStorage.setItem("recentWorkspace", "")
            router.navigate({ path: "/" })
        }
    }

    //Remove Collaborators Handlers
    if (workspaceConfirmationType === WorkspaceConfirmationModalName.RemoveCollaborators) {
        onConfirm = async () => {
            if (!collaborators) return
            await Promise.all(
                collaborators.map(async (collaborator) => {
                    await dispatch(
                        removeMemberFromWorkspace({
                            workspaceId: entityId!,
                            userId: collaborator.userId,
                            isSelf: false,
                        })
                    )
                })
            )
        }
    }
    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteProject) {
        onConfirm = () => dispatch(deleteFire(entityId!))
    }
    //Delete Folder Handlers
    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteFolder) {
        onConfirm = async () => {
            if (!activeWorkspaceId || !entityId) return

            // If deleting the folder or ancestor folder of the one you're in, go up the
            // tag chain until you get to a safe folder, and move there.
            if (workspaceFilteredTagIds && workspaceFilteredTagIds.includes(entityId)) {
                const index = workspaceFilteredTagIds.indexOf(entityId)
                const newTagChain = workspaceFilteredTagIds.slice(0, index)
                dispatch(setFilteredTagIds(newTagChain))
                router.navigate({
                    path: "/workspace/:workspaceId/explore/ws/*",
                    params: { workspaceId: activeWorkspaceId },
                    splat: newTagChain.join("/"),
                })
                dispatch(setActiveTagAction(newTagChain.at(-1)))
            }

            // The children of this folder, including this folder itself.
            const { projectIds, subChains } = allSubEntitiesByFolderId(entityId)

            await Promise.all([
                ...projectIds.map((projectId) => {
                    dispatch(deleteFire(projectId!))
                }),
                ...subChains.map((chain) =>
                    dispatch(
                        deleteTag({
                            id: chain.at(-1)!,
                            activeWorkspaceId: activeWorkspaceId,
                        })
                    )
                ),
            ])
        }
    }
    //Delete Workspace Handlers
    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteWorkspace) {
        onConfirm = async () => {
            if (!activeWorkspaceId) {
                return
            }
            localStorage.setItem("recentWorkspace", "")
            dispatch(resetWorkspaceTree())
            await dispatch(deleteWorkspace(activeWorkspaceId))
            router.navigate({ path: "/" })
        }
    }

    if (workspaceConfirmationType === WorkspaceConfirmationModalName.UnassignSubscription) {
        onConfirm = () => {
            if (!license) return
            dispatch(
                updateLicense({
                    licenseId: license.licenseId,
                    ownerId: license.ownerId,
                    attributes: { assigneeId: "unassigned" },
                })
            )
        }
    }

    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DisconnectIntegration) {
        onConfirm = () => {
            if (!entityId || !secondaryKey) return

            deleteIntegrationAuthorization({
                workspaceId: activeWorkspaceId!,
                authorizationId: entityId,
                authUserId: secondaryKey,
            })
                .then(() => {
                    dispatch(
                        setGlobalNotificationContext({
                            message: `You have been disconnected from ${entityName}.`,
                            status: GlobalNotificationStatus.default,
                            timeoutInMs: 3000,
                        })
                    )
                })
                .then(() => invalidateIntegrationAuthorizations())
        }
    }

    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteHotspot) {
        onConfirm = () => {
            if (!entityId || !secondaryKey) return

            dispatch(
                updateHotspot({
                    id: entityId,
                    context: { flightId: secondaryKey },
                    updates: { status: HotspotStatus.Removed },
                })
            )
        }
    }

    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteMultipleHotspots) {
        onConfirm = () => {
            if (!entityId || !entityIds) return

            for (const id of multiSelectFlightIds) {
                const removingHotspots = multiSelectedHotspots
                    .filter((hotspot) => hotspot.flightId === id)
                    .map((h) => h.id)

                if (removingHotspots.length === 0) continue

                dispatch(
                    batchUpdateHotspotsAction({
                        context: { flightId: id },
                        ids: removingHotspots,
                        updates: { status: HotspotStatus.Removed },
                    })
                )

                dispatch(closeHotspotMultiRemoval())
            }
        }
    }

    if (workspaceConfirmationType === WorkspaceConfirmationModalName.DeleteColorFinderComment) {
        onConfirm = () => {
            if (!entityId) return

            deleteComment({ commentId: entityId })
        }
    }

    return <WorkspaceConfirmationModal {...props} onConfirm={onConfirm} onCancel={onCancel} />
}
